import {
  Icon,
  PageContainer,
  RouteIconButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiEye } from "@mdi/js";
import {
  Alert,
  Box,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
} from "@mui/material";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useListCreatePurchaseOrderByCallOffContract } from "../../../__generated__/orval/endpoints/create-purchase-order-by-call-off-contract/create-purchase-order-by-call-off-contract";
import { EntitiesCreatePurchaseOrderByCallOffContractCollectionListResponse } from "../../../__generated__/orval/models/entitiesCreatePurchaseOrderByCallOffContractCollectionListResponse.zod";
import { CommonErrorHelp } from "../../../components/molecules/CommonErrorHelp";
import { i18nextParseToDate } from "../../../utils/i18nextParseTo";

const statuses = ["processing", "completed", "failed"] as const;
type ExecutionStatus = (typeof statuses)[number];
const groupTrackingNumber = (items: { tracking_number?: unknown }[]) => {
  const trackingNumber = items[0]?.tracking_number;
  return typeof trackingNumber === "string" &&
    /^45\d{8}$/.test(trackingNumber) &&
    items.every((item) => item.tracking_number === trackingNumber)
    ? trackingNumber
    : "-";
};

export const executionSearchSchema = z.object({
  itemsPerPage: z.number().int().min(1).max(100).catch(25).default(25),
  page: z.number().int().min(1).catch(1).default(1),
  status: z.enum(statuses).catch("processing").default("processing"),
});

const ExecutionList = () => {
  const { t } = useTranslation();
  const { status, page, itemsPerPage } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data, isPending, isError } =
    useListCreatePurchaseOrderByCallOffContract({
      execution_status: status,
      items_per_page: itemsPerPage,
      order: "desc",
      page,
    });
  const parsed =
    EntitiesCreatePurchaseOrderByCallOffContractCollectionListResponse.safeParse(
      data,
    );

  return (
    <PageContainer title={t("callOffContract.title")}>
      <Box
        sx={{
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Tabs
          onChange={(_event, value: ExecutionStatus) => {
            navigate({
              search: (previous) => ({ ...previous, page: 1, status: value }),
            });
          }}
          value={status}
        >
          {statuses.map((value) => (
            <Tab
              key={value}
              label={t(`callOffContract.tabs.${value}`)}
              value={value}
            />
          ))}
        </Tabs>
        <CommonErrorHelp errorType="create_purchase_order_by_call_off_contract" />
      </Box>
      {isError || (!isPending && !parsed.success) ? (
        <Alert severity="error">{t("callOffContract.loadError")}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("createPurchaseOrderByQuotation.createdAt")}
                </TableCell>
                <TableCell>
                  {t("createPurchaseOrderByQuotation.updatedAt")}
                </TableCell>
                <TableCell>{t("callOffContract.trackingNumber")}</TableCell>
                <TableCell align="center">
                  {t("callOffContract.itemsInGroup")}
                </TableCell>
                <TableCell align="center" />
              </TableRow>
            </TableHead>
            <TableBody>
              {isPending ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ) : parsed.success && parsed.data.results.length ? (
                parsed.data.results.map((batch) => (
                  <TableRow key={batch.id}>
                    <TableCell>
                      {t("intlDateTime", i18nextParseToDate(batch.created_at))}
                    </TableCell>
                    <TableCell>
                      {t("intlDateTime", i18nextParseToDate(batch.updated_at))}
                    </TableCell>
                    <TableCell>
                      {groupTrackingNumber(batch.call_off_items)}
                    </TableCell>
                    <TableCell align="center">
                      {batch.call_off_items.length}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t("view")}>
                        <RouteIconButtonLink
                          aria-label={t("view")}
                          params={{
                            createPurchaseOrderByCallOffContractId: batch.id,
                          }}
                          search={{ itemsPerPage, page, status }}
                          to="/create_purchase_order_by_call_off_contracts/$createPurchaseOrderByCallOffContractId"
                        >
                          <Icon icon={mdiEye} />
                        </RouteIconButtonLink>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>
                    {t("callOffContract.noItems")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={parsed.success ? parsed.data.total_items : 0}
                  onPageChange={(_event, value) =>
                    navigate({
                      search: (previous) => ({ ...previous, page: value + 1 }),
                    })
                  }
                  onRowsPerPageChange={(event) => {
                    navigate({
                      search: (previous) => ({
                        ...previous,
                        itemsPerPage: Number(event.target.value),
                        page: 1,
                      }),
                    });
                  }}
                  page={page - 1}
                  rowsPerPage={itemsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  showFirstButton
                  showLastButton
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/create_purchase_order_by_call_off_contracts/",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: executionSearchSchema,
  search: {
    middlewares: [stripSearchParams({ itemsPerPage: 25, page: 1 })],
  },
  component: ExecutionList,
});
